<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-mask" @click.self="handleClose">
      <div class="modal-panel" role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
        <button type="button" class="close-btn" aria-label="关闭" @click="handleClose">
          <i class="bi bi-x-lg"></i>
        </button>

        <div class="modal-layout">
          <aside class="promo-aside">
            <InviteRewardPromoPanel
              layout="stack"
              :new-member-reward="promo.newMemberReward"
              :inviter-reward="promo.inviterReward"
            />
          </aside>

          <div class="login-main">
            <div class="login-header">
              <h2 id="login-modal-title">微信登录</h2>
              <p class="login-desc">登录后即可使用降重、降 AIGC、双降等功能</p>
            </div>

            <div v-if="isWeChat" class="oauth-panel">
              <p class="status-text">{{ wechatStatusText }}</p>
              <div class="invite-field">
                <label class="invite-label" for="oauth-invite-code">邀请码（选填）</label>
                <input
                  id="oauth-invite-code"
                  v-model="inviteCodeInput"
                  type="text"
                  class="invite-input"
                  placeholder="如有好友邀请码请填写"
                  maxlength="16"
                  @input="onInviteInput"
                />
              </div>
              <button type="button" class="btn-primary block" @click="startWeChatOAuth">
                微信授权登录
              </button>
            </div>

            <div v-else class="scan-panel">
              <div
                class="qr-wrap"
                :class="{ clickable: !loadingQr, loading: loadingQr }"
                title="点击刷新二维码"
                @click="handleQrClick"
              >
                <img v-if="qrUrl" :src="qrUrl" alt="扫码登录" class="qr-image" />
                <div v-else class="qr-placeholder">
                  <span>{{ loadingQr ? '加载中...' : '点击加载二维码' }}</span>
                </div>
              </div>
              <p class="status-text">{{ scanStatusText }}</p>
              <p class="scan-tip">请使用微信扫一扫关注公众号并完成登录，点击图片可刷新二维码</p>

              <div class="invite-field">
                <label class="invite-label" for="login-invite-code">邀请码（选填）</label>
                <input
                  id="login-invite-code"
                  v-model="inviteCodeInput"
                  type="text"
                  class="invite-input"
                  placeholder="如有好友邀请码请填写"
                  maxlength="16"
                  @input="onInviteInput"
                />
              </div>
            </div>

            <label class="agreement-row">
              <input v-model="agreementAccepted" type="checkbox" class="agreement-checkbox" />
              <span class="agreement-text">
                我已阅读并同意
                <router-link to="/terms" target="_blank" class="legal-link" @click.native.stop>《服务条款》</router-link>
                和
                <router-link to="/privacy" target="_blank" class="legal-link" @click.native.stop>《隐私政策》</router-link>
                ，新用户将自动注册并开通使用资格
              </span>
            </label>

            <div v-if="devLoginAvailable" class="dev-login-panel">
              <div class="dev-login-row">
                <input
                  v-model.trim="devUsername"
                  type="text"
                  class="dev-username-input"
                  placeholder="username"
                  autocomplete="username"
                  @keyup.enter="handleDevLogin">
                <button type="button" class="btn-dev" :disabled="devLoading || !devUsername" @click="handleDevLogin">
                  {{ devLoading ? '登录中...' : '开发登录' }}
                </button>
              </div>
            </div>

            <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import InviteRewardPromoPanel from '@/components/auth/InviteRewardPromoPanel.vue'
import { getInvitePromo } from '@/api/portal/invite'
import { initWeChatScan, getWeChatScanStatus, exchangeWeChatScan, buildWeChatOAuthUrl, devLogin, isDevLoginAvailable } from '@/api/portal/auth'
import { setToken, setMember, getToken } from '@/utils/auth'
import { getStoredInviteCode, setStoredInviteCode } from '@/utils/invite'
import { onLoginOpen, offLoginOpen, closeLogin, emitLoginSuccess } from '@/utils/loginModal'

const SCAN_POLL_MS = 2000

export default {
  name: 'LoginModal',
  components: { InviteRewardPromoPanel },
  data() {
    return {
      visible: false,
      redirectPath: '/workbench',
      isWeChat: false,
      loadingQr: false,
      qrUrl: '',
      ticket: '',
      scanStatusText: '请使用微信扫描二维码',
      wechatStatusText: '正在跳转微信授权...',
      oauthError: false,
      errorMsg: '',
      pollTimer: null,
      devLoading: false,
      devUsername: '',
      devLoginAvailable: isDevLoginAvailable,
      inviteCodeInput: '',
      agreementAccepted: true,
      promo: {
        newMemberReward: 2000,
        inviterReward: 1000
      }
    }
  },
  mounted() {
    onLoginOpen(this.handleOpen)
  },
  beforeDestroy() {
    offLoginOpen(this.handleOpen)
    this.stopPoll()
  },
  methods: {
    handleOpen({ redirect }) {
      if (getToken()) {
        return
      }
      this.redirectPath = redirect || '/workbench'
      this.errorMsg = ''
      this.inviteCodeInput = getStoredInviteCode()
      this.agreementAccepted = true
      this.oauthError = false
      this.visible = true
      this.loadPromo()
      this.isWeChat = /MicroMessenger/i.test(navigator.userAgent)
      if (this.isWeChat) {
        this.wechatStatusText = '请勾选下方协议后点击授权登录'
      } else {
        this.refreshQr()
      }
    },
    loadPromo() {
      getInvitePromo()
        .then(res => {
          if (res.data) {
            this.promo = res.data
          }
        })
        .catch(() => {})
    },
    ensureAgreement() {
      if (!this.agreementAccepted) {
        this.errorMsg = '请先阅读并同意服务条款与隐私政策'
        return false
      }
      this.errorMsg = ''
      return true
    },
    handleQrClick() {
      if (this.loadingQr) {
        return
      }
      this.refreshQr()
    },
    handleClose() {
      this.visible = false
      this.stopPoll()
      closeLogin()
    },
    startWeChatOAuth() {
      if (!this.ensureAgreement()) {
        return
      }
      this.oauthError = false
      this.wechatStatusText = '正在跳转微信授权...'
      window.location.href = buildWeChatOAuthUrl(this.redirectPath, this.inviteCodeInput.trim())
    },
    onInviteInput() {
      setStoredInviteCode(this.inviteCodeInput)
    },
    refreshQr() {
      this.stopPoll()
      this.loadingQr = true
      this.errorMsg = ''
      this.scanStatusText = '正在生成二维码...'
      initWeChatScan(this.inviteCodeInput.trim())
        .then(res => {
          const data = res.data || {}
          this.ticket = data.ticket
          this.qrUrl = data.qrUrl
          this.scanStatusText = '请使用微信扫描二维码'
          this.startPoll()
        })
        .catch(err => {
          this.qrUrl = ''
          this.errorMsg = err.message || '二维码加载失败'
          this.scanStatusText = '请稍后重试'
        })
        .finally(() => {
          this.loadingQr = false
        })
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(this.checkScanStatus, SCAN_POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    checkScanStatus() {
      if (!this.ticket) {
        return
      }
      getWeChatScanStatus(this.ticket)
        .then(res => {
          const data = res.data || {}
          if (data.status === 'confirmed' && data.exchangeCode) {
            this.stopPoll()
            this.scanStatusText = '扫码成功，正在登录...'
            this.finishScanLogin(data.exchangeCode)
          } else if (data.status === 'expired') {
            this.stopPoll()
            this.scanStatusText = '二维码已过期'
            this.errorMsg = '二维码已过期，请刷新'
          }
        })
        .catch(() => {})
    },
    finishScanLogin(exchangeCode) {
      if (!this.ensureAgreement()) {
        return
      }
      exchangeWeChatScan({ ticket: this.ticket, exchangeCode })
        .then(res => {
          setToken(res.token)
          setMember(res.member)
          this.visible = false
          this.stopPoll()
          emitLoginSuccess()
        })
        .catch(err => {
          this.errorMsg = err.message || '登录失败'
          this.scanStatusText = '登录失败，请刷新二维码重试'
          this.refreshQr()
        })
    },
    handleDevLogin() {
      if (!this.ensureAgreement()) {
        return
      }
      if (!this.devUsername) {
        this.errorMsg = '请输入 username'
        return
      }
      this.devLoading = true
      this.errorMsg = ''
      devLogin({
        username: this.devUsername,
        inviteCode: this.inviteCodeInput.trim()
      })
        .then(res => {
          setToken(res.token)
          setMember(res.member)
          this.visible = false
          this.stopPoll()
          emitLoginSuccess()
        })
        .catch(err => {
          this.errorMsg = err.message || '开发登录失败，请确认后端已启用 portal.dev-login.enabled'
        })
        .finally(() => {
          this.devLoading = false
        })
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-panel {
  position: relative;
  width: min(860px, 100%);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.modal-layout {
  display: flex;
  min-height: 520px;
}

.promo-aside {
  width: 360px;
  flex-shrink: 0;
  border-right: 1px solid rgba(25, 118, 210, 0.08);
}

.login-main {
  flex: 1;
  min-width: 0;
  padding: 32px 32px 28px;
  display: flex;
  flex-direction: column;
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 3;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.close-btn:hover {
  background: #fff;
  color: #111827;
}

.login-header {
  text-align: center;
  margin-bottom: 20px;
}

.login-header h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: #111827;
}

.login-desc {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.qr-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.qr-wrap.clickable {
  cursor: pointer;
}

.qr-wrap.clickable:hover .qr-image {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.12);
}

.qr-wrap.loading {
  cursor: wait;
  opacity: 0.7;
}

.qr-image {
  width: 200px;
  height: 200px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.qr-placeholder {
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  color: var(--text-muted);
  font-size: 14px;
}

.status-text {
  margin: 0 0 8px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.scan-tip {
  margin: 0 0 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 12px;
}

.agreement-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: auto;
  padding-top: 16px;
  cursor: pointer;
  user-select: none;
}

.agreement-checkbox {
  margin-top: 3px;
  flex-shrink: 0;
}

.agreement-text {
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-secondary);
}

.legal-link {
  color: #2563eb;
  text-decoration: none;
}

.legal-link:hover {
  text-decoration: underline;
}

.btn-primary {
  height: 40px;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.btn-primary.block {
  width: 100%;
  margin-top: 12px;
}

.oauth-panel {
  padding: 8px 0;
}

.error-msg {
  margin: 14px 0 0;
  color: #ef4444;
  font-size: 14px;
  text-align: center;
}

.invite-field {
  margin-top: 16px;
  text-align: left;
}

.invite-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: var(--text-secondary);
}

.invite-input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
}

.invite-input:focus {
  outline: none;
  border-color: var(--primary);
}

.dev-login-panel {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.dev-tip {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
}

.dev-login-row {
  display: flex;
  gap: 8px;
}

.dev-username-input {
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
}

.dev-username-input:focus {
  outline: none;
  border-color: #c4b5fd;
}

.btn-dev {
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px dashed #c4b5fd;
  background: #f0f7ff;
  color: #1565c0;
  font-size: 14px;
  cursor: pointer;
}

.btn-dev:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 720px) {
  .modal-layout {
    flex-direction: column;
    min-height: auto;
  }

  .promo-aside {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(25, 118, 210, 0.08);
  }

  .login-main {
    padding: 24px 20px 20px;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
